import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import { type ModalState } from '../utils/types';

interface ModalContextProps {
  modal: string | null;
  setModal: (modal: string | null, state?: ModalState) => void;
  clearModalRefreshFlags: () => void;
  modalState?: ModalState | null;
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined);
export const COMPLETE_BUILDING_MODAL_KEY = 'completeBuildingModal';

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [modal, setModalState] = useState<string | null>(null);
  const [modalState, setModalStateData] = useState<ModalState | null>(null);

  // Restore complete-building modal state from localStorage on mount
  useEffect(() => {
    try {
      const savedModal = localStorage.getItem(COMPLETE_BUILDING_MODAL_KEY);
      if (savedModal) {
        const { modal: savedModalName, state: savedModalState } = JSON.parse(savedModal);
        setModalState(savedModalName);
        if (savedModalState) {
          setModalStateData(savedModalState);
        }
      }
    } catch (error) {
      console.error('Error restoring modal state:', error);
    }
  }, []);

  const setModal = useCallback((next: string | null, state?: ModalState) => {
    setModalState(next);
    if (state) {
      setModalStateData(state);
    }

    // Persist complete-building modal state to localStorage
    if (next === 'complete-building' && state) {
      try {
        localStorage.setItem(
          COMPLETE_BUILDING_MODAL_KEY,
          JSON.stringify({ modal: next, state })
        );
      } catch (error) {
        console.error('Error saving modal state:', error);
      }
    } else {
      // Clear persisted modal state when modal is closed or switched to another modal
      try {
        localStorage.removeItem(COMPLETE_BUILDING_MODAL_KEY);
      } catch (error) {
        console.error('Error clearing modal state:', error);
      }
    }
  }, []);

  const clearModalRefreshFlags = useCallback(() => {
    setModalStateData((prev) => {
      if (!prev) return null;
      const rest = { ...prev };
      delete rest.refreshProperties;
      delete rest.refreshProfile;
      delete rest.refetchPaymentSummary;

      delete rest.refreshTransactions;

      return Object.keys(rest).length > 0 ? rest : null;
    });
  }, []);

  // Single scroll lock for the whole app — avoids dozens of <Modal> instances fighting over overflow.
  useLayoutEffect(() => {
    if (modal == null) {
      return;
    }
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, [modal]);

  const value = useMemo(
    () => ({ modal, setModal, clearModalRefreshFlags, modalState }),
    [modal, setModal, clearModalRefreshFlags, modalState]
  );

  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (context == null) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};
